
import { supabase } from "@/integrations/supabase/client";

interface GeocodeResponse {
  features: Array<{
    center: [number, number];
  }>;
}

const stateMap: { [key: string]: string } = {
  'arizona': 'AZ',
  'alaska': 'AK',
  'alabama': 'AL',
  'arkansas': 'AR',
  'california': 'CA',
  'colorado': 'CO',
  'connecticut': 'CT',
  'delaware': 'DE',
  'florida': 'FL',
  'georgia': 'GA',
  'hawaii': 'HI',
  'idaho': 'ID',
  'illinois': 'IL',
  'indiana': 'IN',
  'iowa': 'IA',
  'kansas': 'KS',
  'kentucky': 'KY',
  'louisiana': 'LA',
  'maine': 'ME',
  'maryland': 'MD',
  'massachusetts': 'MA',
  'michigan': 'MI',
  'minnesota': 'MN',
  'mississippi': 'MS',
  'missouri': 'MO',
  'montana': 'MT',
  'nebraska': 'NE',
  'nevada': 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  'ohio': 'OH',
  'oklahoma': 'OK',
  'oregon': 'OR',
  'pennsylvania': 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  'tennessee': 'TN',
  'texas': 'TX',
  'utah': 'UT',
  'vermont': 'VT',
  'virginia': 'VA',
  'washington': 'WA',
  'west virginia': 'WV',
  'wisconsin': 'WI',
  'wyoming': 'WY'
};

const normalizeState = (state: string): string => {
  const normalized = state.toLowerCase().trim();
  if (/^[a-z]{2}$/.test(normalized)) {
    return normalized.toUpperCase();
  }
  return stateMap[normalized] || state;
};

export const geocodeAddress = async (
  address: string,
  city: string,
  state: string,
  postalCode: string
): Promise<{ latitude: number; longitude: number } | null> => {
  if (!address || !city || !state || !postalCode) {
    console.log('Skipping geocoding - incomplete address:', { address, city, state, postalCode });
    return null;
  }

  try {
    const tokenResponse = await fetch('/functions/v1/get-mapbox-token', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!tokenResponse.ok) {
      console.error('Failed to fetch Mapbox token:', tokenResponse.status);
      return null;
    }

    const tokenData = await tokenResponse.json();
    const token = tokenData?.data?.token;

    if (!token) {
      console.error('No Mapbox token available');
      return null;
    }

    const normalizedState = normalizeState(state);
    
    const searchQuery = [
      address.trim(),
      city.trim(),
      normalizedState,
      postalCode.trim()
    ]
      .filter(Boolean)
      .join(', ');
    
    const encodedQuery = encodeURIComponent(searchQuery);
    const mapboxUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?access_token=${token}&country=US&types=address`;
    
    console.log('Geocoding query:', searchQuery);
    
    const geocodeResponse = await fetch(mapboxUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (!geocodeResponse.ok) {
      console.error('Geocoding request failed:', geocodeResponse.status);
      return null;
    }

    const data = await geocodeResponse.json() as GeocodeResponse;
    
    if (!data?.features?.length) {
      console.error('No geocoding results found for address:', searchQuery);
      return null;
    }
    
    const [longitude, latitude] = data.features[0].center;
    console.log('Successfully geocoded address:', {
      query: searchQuery,
      coordinates: { latitude, longitude }
    });
    
    return { latitude, longitude };
  } catch (error) {
    console.error('Error in geocoding process:', {
      error,
      address,
      city,
      state,
      postalCode
    });
    return null;
  }
};

export const geocodeAllAddresses = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('geocode-address', {
      method: 'GET',
    });

    if (error) {
      console.error('Error invoking geocode-address function:', error);
      throw error;
    }

    console.log('Geocoding response:', data);
    return data;
  } catch (error) {
    console.error('Error in geocodeAllAddresses:', error);
    throw error;
  }
};
